import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { aiError } from "@/hooks/useAssistant";

export const VoiceInput = ({ disabled, onText, onError, onActive }) => {
  const { lang, t } = useApp();
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorder = useRef(null), stream = useRef(null), timer = useRef(null), discard = useRef(false), controller = useRef(null);
  useEffect(() => () => { discard.current = true; clearTimeout(timer.current); recorder.current?.state === "recording" && recorder.current.stop(); stream.current?.getTracks().forEach((track) => track.stop()); controller.current?.abort(); }, []);
  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return onError("ai_record_unsupported");
    try {
      discard.current = false;
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (discard.current) { stream.current.getTracks().forEach((track) => track.stop()); return; }
      const mimeType = ["audio/webm", "audio/mp4"].find((type) => MediaRecorder.isTypeSupported(type));
      if (!mimeType) { stream.current.getTracks().forEach((track) => track.stop()); return onError("ai_record_unsupported"); }
      const chunks = [];
      const media = new MediaRecorder(stream.current, { mimeType });
      recorder.current = media;
      media.ondataavailable = (event) => event.data.size && chunks.push(event.data);
      media.onstop = async () => {
        clearTimeout(timer.current); stream.current.getTracks().forEach((track) => track.stop());
        if (discard.current) return;
        setRecording(false); setBusy(true); controller.current = new AbortController();
        try {
          const { data } = await api.post(`/ai/transcribe?lang=${lang}`, new Blob(chunks, { type: mimeType }), { headers: { "Content-Type": mimeType }, signal: controller.current.signal });
          if (!discard.current) onText(data.text);
        } catch (e) { if (!discard.current) onError(aiError(e)); } finally { if (!discard.current) { setBusy(false); onActive(false); } }
      };
      media.start(); setRecording(true); onActive(true); onError("");
      timer.current = setTimeout(() => { if (media.state === "recording") media.stop(); }, 60000);
    } catch { onError("ai_mic_denied"); }
  };
  return <button data-testid="ai-microphone" type="button" title={t(recording ? "ai_record_stop" : busy ? "ai_transcribing" : "ai_record")} aria-label={t(recording ? "ai_record_stop" : "ai_record")} disabled={disabled || busy} onClick={() => recording ? recorder.current.stop() : start()} className={`grid h-10 w-10 shrink-0 place-items-center rounded-full transition-colors disabled:opacity-40 ${recording ? "animate-pulse bg-red-100 text-red-700" : "text-sea hover:bg-sea-light"}`}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-5 w-5" />}
  </button>;
};